import React from 'react';
import clsx from 'clsx';

import { Link } from '@edx/components/link';
import { IconPlaySolid, IconAngleRight } from '@edx/components/icons';

import { VideoEntryProps } from './';

type Props = {
  items: VideoEntryProps[];
  variant?: 'simple';
  hidePlayButton?: boolean;
};

export const VideoEntries: React.FC<Props> = ({ items, variant, hidePlayButton }) => {
  const isSimpleVariant = variant === 'simple';

  return (
    <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
      {items.map(({ name, tagline, img, resourcePath, videosCount, viewerCompletionPercentage }) => (
        <li
          key={resourcePath}
          className="relative group overflow-hidden hover:bg-gray-600 hover:bg-opacity-10 transition duration-150"
        >
          <Link href={resourcePath}>
            <div className={clsx('relative group', { 'h-48': isSimpleVariant })}>
              {viewerCompletionPercentage ? (
                <span
                  className="z-10 absolute top-0 bg-primary-500 h-2px"
                  style={{ width: `${viewerCompletionPercentage}%` }}
                />
              ) : null}

              {/* @TODO: This is the path variant, needs to be refactored */}
              {variant === 'simple' ? (
                <div className="relative aspect-ratio-21/9 md:aspect-ratio-square">
                  <picture>
                    <source media="(min-width: 769px)" srcSet={img.large} />
                    <img src={img.medium} alt={name} className="absolute" />
                  </picture>
                </div>
              ) : typeof img === 'string' ? (
                /* @TODO Use Next Image component here */
                <img
                  className={clsx({ 'h-auto md:h-full w-full md:w-auto object-cover': isSimpleVariant })}
                  loading="eager"
                  src={img}
                  alt={name}
                />
              ) : (
                /* @TODO Implement responsive image here */
                <picture>
                  <source media="(min-width: 1122px)" srcSet={img.large} />
                  <source media="(min-width: 769px)" srcSet={img.medium} />
                  <img
                    className={clsx({ 'h-auto md:h-full w-full object-cover object-top': isSimpleVariant })}
                    src={img.small}
                    alt=""
                  />
                </picture>
              )}

              <div
                className={clsx(
                  'absolute inset-0 flex bg-gray-800 bg-opacity-50 group-hover:bg-opacity-0 transition duration-150',
                  isSimpleVariant ? 'flex-col justify-between p-2' : 'items-center justify-center'
                )}
              >
                {!hidePlayButton && (
                  <div className="flex">
                    <div className="p-2 bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-all duration-200 ease transform translate-y-3 group-hover:translate-y-0">
                      <IconPlaySolid className="text-primary-500 w-6" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              className={clsx(
                'w-full flex flex-col font-body text-gray-500 space-y-2',
                hidePlayButton ? 'top-0' : 'bottom-0',
                isSimpleVariant ? 'absolute p-4' : 'p-2'
              )}
            >
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <header className="text-4xl md:text-3xl lg:text-4xl uppercase font-display text-gray-300 leading-tight">
                    {name}
                  </header>

                  <span className="text-xs uppercase tracking-widest font-medium leading-relaxed">
                    {videosCount} Videos
                  </span>
                </div>

                {viewerCompletionPercentage ? (
                  <span className="uppercase font-body text-gray-400">{viewerCompletionPercentage}%</span>
                ) : null}
              </div>

              {tagline && <p>{tagline}</p>}

              {!isSimpleVariant && (
                <footer className="block md:hidden text-sm text-gray-300 font-medium flex">
                  Acceder <IconAngleRight className="w-3" />
                </footer>
              )}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
};
